import { ApiResponse, apiRequest } from '../request'

export type HealthMapQuestionType =
    | 'YES_NO'
    | 'TEXT'
    | 'MULTIPLE_CHOICE'
    | 'SINGLE_CHOICE'

export type HealthMapResponseStatus = 'DRAFT' | 'COMPLETED'
export type HealthMapFilledBy = 'PROFESSIONAL' | 'PATIENT'

export const HEALTH_MAP_QUESTION_TYPE_LABELS: Record<
    HealthMapQuestionType,
    string
> = {
    YES_NO: 'Sim/Não',
    TEXT: 'Texto',
    MULTIPLE_CHOICE: 'Múltipla escolha',
    SINGLE_CHOICE: 'Escolha única',
}

export interface HealthMapQuestion {
    id: number
    themeId: number
    title: string
    helpText?: string | null
    type: HealthMapQuestionType
    /** Alternativas de MULTIPLE_CHOICE e SINGLE_CHOICE. */
    options?: string[] | null
    required: boolean
    sortOrder: number
    /** Pergunta padrão da plataforma: não pode ser editada nem excluída. */
    isSystem: boolean
    active: boolean
}

export interface HealthMapTheme {
    id: number
    categoryId: number
    name: string
    description?: string | null
    sortOrder: number
    isSystem: boolean
    active: boolean
    questions: HealthMapQuestion[]
}

export interface HealthMapCategory {
    id: number
    name: string
    description?: string | null
    icon?: string | null
    sortOrder: number
    isSystem: boolean
    active: boolean
    themes: HealthMapTheme[]
}

export interface HealthMapAnswer {
    id: number
    responseId: number
    questionId: number
    /** Enunciado congelado no momento da resposta. */
    questionTitle: string
    questionType: HealthMapQuestionType
    categoryName: string
    themeName: string
    booleanValue?: boolean | null
    textValue?: string | null
    selectedOptions?: string[] | null
}

export interface HealthMapResponse {
    id: number
    clientId: number
    companyId: number
    status: HealthMapResponseStatus
    filledBy: HealthMapFilledBy
    professionalId?: number | null
    professional?: { id: number; name: string } | null
    createdBy?: { id: number; name: string } | null
    shareLink?: { id: number; token: string; usedAt?: string | null } | null
    startedAt: string
    submittedAt?: string | null
    answers: HealthMapAnswer[]
    createdAt: string
    updatedAt: string
}

export interface HealthMapShareLink {
    id: number
    clientId: number
    companyId: number
    token: string
    /** URL pronta para copiar/compartilhar, montada pelo backend. */
    url: string
    createdBy?: { id: number; name: string } | null
    expiresAt: string
    openedAt?: string | null
    usedAt?: string | null
    revokedAt?: string | null
    response?: { id: number; status: HealthMapResponseStatus } | null
    createdAt: string
}

/** O que o front envia ao salvar — vazio em todos os campos APAGA a resposta. */
export interface HealthMapAnswerInput {
    questionId: number
    booleanValue?: boolean | null
    textValue?: string | null
    selectedOptions?: string[]
}

// ============================================================================
// Estrutura (categorias > temas > perguntas)
// ============================================================================

export async function getHealthMapStructure(
    includeInactive = false
): Promise<ApiResponse<HealthMapCategory[]>> {
    return apiRequest<HealthMapCategory[]>('health-map/structure', {
        params: includeInactive ? { includeInactive: 'true' } : {},
    })
}

export async function createHealthMapCategory(data: {
    name: string
    description?: string
    icon?: string
    sortOrder?: number
}): Promise<ApiResponse<HealthMapCategory>> {
    return apiRequest<HealthMapCategory>('health-map/categories', { data }, 'post')
}

export async function updateHealthMapCategory(
    id: number,
    data: Partial<{
        name: string
        description: string
        icon: string
        sortOrder: number
        active: boolean
    }>
): Promise<ApiResponse<HealthMapCategory>> {
    return apiRequest<HealthMapCategory>(
        `health-map/categories/${id}`,
        { data },
        'patch'
    )
}

export async function deleteHealthMapCategory(
    id: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `health-map/categories/${id}`,
        {},
        'delete'
    )
}

export async function createHealthMapTheme(data: {
    categoryId: number
    name: string
    description?: string
    sortOrder?: number
}): Promise<ApiResponse<HealthMapTheme>> {
    return apiRequest<HealthMapTheme>('health-map/themes', { data }, 'post')
}

export async function updateHealthMapTheme(
    id: number,
    data: Partial<{
        categoryId: number
        name: string
        description: string
        sortOrder: number
        active: boolean
    }>
): Promise<ApiResponse<HealthMapTheme>> {
    return apiRequest<HealthMapTheme>(
        `health-map/themes/${id}`,
        { data },
        'patch'
    )
}

export async function deleteHealthMapTheme(
    id: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `health-map/themes/${id}`,
        {},
        'delete'
    )
}

export async function createHealthMapQuestion(data: {
    themeId: number
    title: string
    helpText?: string
    type: HealthMapQuestionType
    options?: string[]
    required?: boolean
    sortOrder?: number
}): Promise<ApiResponse<HealthMapQuestion>> {
    return apiRequest<HealthMapQuestion>('health-map/questions', { data }, 'post')
}

export async function updateHealthMapQuestion(
    id: number,
    data: Partial<{
        themeId: number
        title: string
        helpText: string
        type: HealthMapQuestionType
        options: string[]
        required: boolean
        sortOrder: number
        active: boolean
    }>
): Promise<ApiResponse<HealthMapQuestion>> {
    return apiRequest<HealthMapQuestion>(
        `health-map/questions/${id}`,
        { data },
        'patch'
    )
}

export async function deleteHealthMapQuestion(
    id: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `health-map/questions/${id}`,
        {},
        'delete'
    )
}

// ============================================================================
// Preenchimentos
// ============================================================================

export async function getHealthMapResponses(
    clientId: number
): Promise<ApiResponse<HealthMapResponse[]>> {
    return apiRequest<HealthMapResponse[]>(
        `health-map/clients/${clientId}/responses`,
        {}
    )
}

export async function startHealthMapResponse(
    clientId: number,
    data: { professionalId?: number } = {}
): Promise<ApiResponse<HealthMapResponse>> {
    return apiRequest<HealthMapResponse>(
        `health-map/clients/${clientId}/responses`,
        { data },
        'post'
    )
}

export async function getHealthMapResponse(
    id: number
): Promise<ApiResponse<HealthMapResponse>> {
    return apiRequest<HealthMapResponse>(`health-map/responses/${id}`, {})
}

export async function saveHealthMapAnswers(
    responseId: number,
    answers: HealthMapAnswerInput[]
): Promise<ApiResponse<HealthMapResponse>> {
    return apiRequest<HealthMapResponse>(
        `health-map/responses/${responseId}/answers`,
        { data: { answers } },
        'patch'
    )
}

export async function submitHealthMapResponse(
    responseId: number
): Promise<ApiResponse<HealthMapResponse>> {
    return apiRequest<HealthMapResponse>(
        `health-map/responses/${responseId}/submit`,
        {},
        'post'
    )
}

export async function deleteHealthMapResponse(
    responseId: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `health-map/responses/${responseId}`,
        {},
        'delete'
    )
}

// ============================================================================
// Links de compartilhamento
// ============================================================================

export async function getHealthMapShareLinks(
    clientId: number
): Promise<ApiResponse<HealthMapShareLink[]>> {
    return apiRequest<HealthMapShareLink[]>(
        `health-map/clients/${clientId}/share-links`,
        {}
    )
}

export async function createHealthMapShareLink(
    clientId: number,
    expiresInDays?: number
): Promise<ApiResponse<HealthMapShareLink>> {
    return apiRequest<HealthMapShareLink>(
        `health-map/clients/${clientId}/share-links`,
        { data: { expiresInDays } },
        'post'
    )
}

export async function sendHealthMap(
    clientId: number,
    expiresInDays?: number
): Promise<ApiResponse<{ link: HealthMapShareLink; message: string }>> {
    return apiRequest<{ link: HealthMapShareLink; message: string }>(
        `health-map/clients/${clientId}/send`,
        { data: { expiresInDays } },
        'post'
    )
}

export async function revokeHealthMapShareLink(
    id: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `health-map/share-links/${id}`,
        {},
        'delete'
    )
}

// ============================================================================
// Link público do paciente (sem login)
// ============================================================================

export interface PublicHealthMapMeta {
    companyName: string | null
    expiresAt: string
    alreadyAnswered: boolean
}

export interface PublicHealthMapView {
    clientName: string
    companyName: string | null
    structure: HealthMapCategory[]
    response: HealthMapResponse
}

export async function getPublicHealthMapMeta(
    token: string
): Promise<ApiResponse<PublicHealthMapMeta>> {
    return apiRequest<PublicHealthMapMeta>(`health-map/p/${token}/meta`, {})
}

export async function startPublicHealthMap(
    token: string,
    cpf: string
): Promise<ApiResponse<PublicHealthMapView & { accessToken: string }>> {
    return apiRequest<PublicHealthMapView & { accessToken: string }>(
        `health-map/p/${token}/start`,
        { data: { cpf } },
        'post'
    )
}

export async function resumePublicHealthMap(
    token: string,
    accessToken: string
): Promise<ApiResponse<PublicHealthMapView>> {
    return apiRequest<PublicHealthMapView>(
        `health-map/p/${token}/resume`,
        { data: { accessToken } },
        'post'
    )
}

export async function savePublicHealthMapAnswers(
    token: string,
    auth: { accessToken?: string; cpf?: string },
    answers: HealthMapAnswerInput[]
): Promise<ApiResponse<HealthMapResponse>> {
    return apiRequest<HealthMapResponse>(
        `health-map/p/${token}/answers`,
        { data: { ...auth, answers } },
        'post'
    )
}

export async function submitPublicHealthMap(
    token: string,
    auth: { accessToken?: string; cpf?: string }
): Promise<ApiResponse<{ message: string; id: number }>> {
    return apiRequest<{ message: string; id: number }>(
        `health-map/p/${token}/submit`,
        { data: auth },
        'post'
    )
}
